import { getUseCase } from '@lib/plugin-redux-core';
import { GET_FOLDER_BY_ID_USE_CASE, SearchQueryParams, SpaceObjects } from '@module/form';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { DEFAULT_FOLDER_ID, useResolvedFolderId } from './use-resolved-folder-id.hook';
import { spaceObjectsSelector } from 'redux/space';
import {
  buildSpaceUrl,
  buildSpaceFolderTrail,
  resolveSpaceFolderTrail,
} from '../../../utils/space-url.util';

export const useSpaceFolder = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const resolvedFolderId = useResolvedFolderId();
  const spaceObjects: SpaceObjects = useSelector(spaceObjectsSelector);

  const getFolderByIdUseCase = useMemo(
    () => getUseCase(GET_FOLDER_BY_ID_USE_CASE, dispatch),
    [dispatch],
  );

  const fetchFolderById = useCallback(
    (folderId: string): void => {
      const query: SearchQueryParams = {
        folderId: folderId || DEFAULT_FOLDER_ID,
        limit: 20,
      };
      getFolderByIdUseCase.execute(query);
    },
    [getFolderByIdUseCase],
  );

  useEffect(() => {
    fetchFolderById(resolvedFolderId);
  }, [resolvedFolderId, fetchFolderById]);

  // Keep folderIds/folderNames in sync once parentFolders load (e.g. deep link with only folder=)
  useEffect(() => {
    if (!spaceObjects?.parentFolders?.length) return;
    if (resolvedFolderId === DEFAULT_FOLDER_ID) return;

    const trailFromParents = buildSpaceFolderTrail(
      spaceObjects.parentFolders.map((folder) => ({
        id: folder.id.toString(),
        title: folder.title,
      })),
    );
    if (!trailFromParents) return;

    const currentIds = router.query.folderIds;
    const currentNames = router.query.folderNames;
    const hasTrailParams = Boolean(currentIds && currentNames);
    if (hasTrailParams) return;

    const url = buildSpaceUrl({
      folderId: trailFromParents.folderId,
      folderIds: trailFromParents.folderIds,
      folderNames: trailFromParents.folderNames,
    });
    router.replace(url, undefined, { shallow: true });
  }, [spaceObjects?.parentFolders, resolvedFolderId, router]);

  const onSelectFolder = useCallback(
    (folderId: string, folderTitle?: string): void => {
      if (resolvedFolderId === folderId) {
        return;
      }

      const currentTrail =
        spaceObjects?.parentFolders?.map((folder) => ({
          id: folder.id.toString(),
          title: folder.title,
        })) ?? [];

      const resolvedTitle =
        folderTitle ||
        spaceObjects?.folders?.find((folder) => folder.id.toString() === folderId)?.title;

      const trail = resolveSpaceFolderTrail({
        currentTrail,
        targetFolderId: folderId,
        targetFolderTitle: resolvedTitle,
      });

      const url = trail
        ? buildSpaceUrl({
            folderId: trail.folderId,
            folderIds: trail.folderIds,
            folderNames: trail.folderNames,
          })
        : buildSpaceUrl();

      router.replace(url, undefined, { shallow: true });
    },
    [resolvedFolderId, router, spaceObjects],
  );

  return { onSelectFolder };
};
